<template>
  <b-container class="calendar px-0 px-sm-3">
    <div class="calendar-head bg-dark text-white">
      <button
        type="button"
        class="calendar-button"
        aria-label="Vorheriger Monat"
        @click="switchMonth(-1)"
      >
        <b-icon-arrow-left />
      </button>
      <h2>{{ calendarHead }}</h2>
      <button
        type="button"
        class="calendar-button"
        aria-label="Nächster Monat"
        @click="switchMonth(1)"
      >
        <b-icon-arrow-right />
      </button>
    </div>

    <div class="weekdays">
      <div v-for="d in WEEKDAYS" :key="d" :class="{ 'text-primary': d === 'So' }">
        {{ d }}
      </div>
    </div>

    <transition :name="'month-slide-' + slideDirection" mode="out-in">
      <div :key="monthKey" class="month">
        <div v-for="(week, wi) in weeks" :key="wi" class="week">
          <div class="week-bg">
            <div
              v-for="day in week.days"
              :key="day.getTime()"
              class="cell"
              :class="{
                'inactive-month': day.getMonth() !== NOW.getMonth(),
                sunday: day.getDay() === 0,
                today: isToday(day),
                selected: selectedDay && isSameDay(day, selectedDay),
              }"
              @click="selectDay(day)"
            >
              <span class="cell-number">{{ day.getDate() }}</span>
            </div>
          </div>
          <div class="week-events" :style="{ gridTemplateRows: week.rows }">
            <Appointment
              v-for="seg in week.layout.segments"
              :key="seg.event.id + '-' + wi"
              :segment="seg"
              :active="!!popover && popover.event === seg.event"
              @select="openDetail"
            />
            <button
              v-for="m in week.layout.more"
              :key="'more-' + m.col"
              type="button"
              class="more"
              :style="{ gridColumn: m.col + 1, gridRow: MAX_LANES + 2 }"
              @click.stop="selectDay(week.days[m.col])"
            >
              +{{ m.count }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="selectedDay" class="day-list">
      <h3 class="day-list-title">{{ selectedDayLabel }}</h3>
      <p v-if="selectedEvents.length === 0" class="text-muted mb-0">
        Keine Termine an diesem Tag.
      </p>
      <button
        v-for="ev in selectedEvents"
        :key="ev.id"
        type="button"
        class="day-list-item"
        @click="openDetail(ev)"
      >
        <span class="item-title">{{ ev.title }}</span>
        <span class="item-meta">{{ formatEventRange(ev) }}</span>
        <span v-if="ev.location" class="item-meta">{{ ev.location }}</span>
      </button>
    </div>

    <b-popover
      v-if="popover"
      :key="popover.event.id"
      :target="popover.el"
      :show="true"
      placement="auto"
      triggers="manual"
      custom-class="event-popover"
      @hidden="closeDetail"
    >
      <AppointmentDetails :appoint="popover.event" @close="closeDetail" />
    </b-popover>

    <b-modal
      v-model="sheetOpen"
      modal-class="event-sheet"
      hide-header
      hide-footer
      body-class="p-0"
      @hidden="sheetEvent = null"
    >
      <AppointmentDetails
        v-if="sheetEvent"
        :appoint="sheetEvent"
        @close="sheetOpen = false"
      />
    </b-modal>
  </b-container>
</template>

<script>
import Appointment from "@/components/calendar/Appointment.vue";
import AppointmentDetails from "@/components/calendar/AppointmentDetails.vue";
import {
  buildWeeks,
  layoutWeek,
  eventsOnDay,
  isSameDay,
  formatEventRange,
} from "@/utils/calendarLayout";

import { mapGetters, mapActions } from "vuex";

const MOBILE_QUERY = "(max-width: 575.98px)";
const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: "Calendar",
  components: {
    Appointment,
    AppointmentDetails,
  },
  data() {
    return {
      MONTH_NAME: [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember",
      ],
      WEEKDAYS: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
      MAX_LANES: 3,
      NOW: new Date(),
      selectedDay: null,
      slideDirection: "next",
      isCompact: false,
      popover: null,
      sheetOpen: false,
      sheetEvent: null,
      mediaQuery: null,
    };
  },
  computed: {
    ...mapGetters(["getAllDates"]),
    weeks() {
      return buildWeeks(this.NOW).map((days) => {
        const layout = layoutWeek(days, this.getAllDates, this.MAX_LANES);
        const rows = this.isCompact ? "26px" : "30px";
        return {
          days,
          layout,
          rows: `${rows} repeat(${Math.max(layout.laneCount, 1)}, auto)`,
        };
      });
    },
    monthKey() {
      return this.NOW.getFullYear() + "-" + this.NOW.getMonth();
    },
    calendarHead() {
      return (
        this.MONTH_NAME[this.NOW.getMonth()] + " " + this.NOW.getFullYear()
      );
    },
    selectedEvents() {
      return this.selectedDay
        ? eventsOnDay(this.getAllDates, this.selectedDay)
        : [];
    },
    selectedDayLabel() {
      return new Intl.DateTimeFormat("de-DE", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(this.selectedDay);
    },
  },

  mounted() {
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.isCompact = this.mediaQuery.matches;
    this.mediaQuery.addListener(this.onMediaChange);
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    this.resetSelection();
    this.loadVisibleRange();
  },

  beforeDestroy() {
    if (this.mediaQuery) this.mediaQuery.removeListener(this.onMediaChange);
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    ...mapActions(["fetchDatesForRange"]),
    isSameDay,
    formatEventRange,
    isToday(day) {
      return isSameDay(day, new Date());
    },
    loadVisibleRange() {
      const weeks = buildWeeks(this.NOW);
      const from = new Date(weeks[0][0].getTime() - 14 * DAY_MS);
      const to = new Date(weeks[5][6].getTime() + 8 * DAY_MS);
      this.fetchDatesForRange({ from, to });
    },
    resetSelection() {
      if (!this.isCompact) {
        this.selectedDay = null;
        return;
      }
      const today = new Date();
      this.selectedDay = isSameDay(
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(this.NOW.getFullYear(), this.NOW.getMonth(), 1)
      )
        ? today
        : new Date(this.NOW.getFullYear(), this.NOW.getMonth(), 1);
    },
    switchMonth(summand) {
      this.closeDetail();
      this.slideDirection = summand > 0 ? "next" : "prev";
      this.NOW = new Date(
        this.NOW.getFullYear(),
        this.NOW.getMonth() + summand,
        1
      );
      this.resetSelection();
      this.loadVisibleRange();
    },
    selectDay(day) {
      this.closeDetail();
      this.selectedDay = new Date(day);
      if (day.getMonth() !== this.NOW.getMonth()) {
        this.slideDirection = day > this.NOW ? "next" : "prev";
        this.NOW = new Date(day.getFullYear(), day.getMonth(), 1);
        this.loadVisibleRange();
      }
    },
    openDetail(event, el) {
      if (el && !this.isCompact) {
        this.popover = { event, el };
      } else {
        this.closeDetail();
        this.sheetEvent = event;
        this.sheetOpen = true;
      }
    },
    closeDetail() {
      this.popover = null;
    },
    onMediaChange(e) {
      this.isCompact = e.matches;
      this.closeDetail();
      this.resetSelection();
    },
    onDocumentClick(e) {
      if (this.popover && !e.target.closest(".event-popover")) {
        this.closeDetail();
      }
    },
    onKeydown(e) {
      if (e.key === "Escape") this.closeDetail();
    },
  },
};
</script>

<style lang="scss" scoped>
.month {
  position: relative;
}
.month-slide-next-enter-active,
.month-slide-next-leave-active,
.month-slide-prev-enter-active,
.month-slide-prev-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.month-slide-next-enter,
.month-slide-prev-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
.month-slide-next-leave-to,
.month-slide-prev-enter {
  opacity: 0;
  transform: translateX(-24px);
}
@media (prefers-reduced-motion: reduce) {
  .month-slide-next-enter-active,
  .month-slide-next-leave-active,
  .month-slide-prev-enter-active,
  .month-slide-prev-leave-active {
    transition: none;
  }
}
$border: #dee2e6;

.calendar {
  max-width: 1140px;
}

.calendar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  h2 {
    margin: 0;
    font-size: 2rem;
  }
}
.calendar-button {
  border: 0;
  background: none;
  color: inherit;
  font-size: 1.75rem;
  line-height: 1;
  width: 44px;
  height: 44px;
  cursor: pointer;
  &:hover {
    color: #999;
  }
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  background: #f8f9fa;
  border: 1px solid $border;
  border-width: 0 1px 1px;
  text-align: center;
  font-weight: bold;
  padding: 6px 0;
}

.week {
  position: relative;
  min-height: 104px;
  border: 1px solid $border;
  border-width: 0 1px 1px;
}
.week-bg,
.week-events {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}
.week-bg {
  position: absolute;
  inset: 0;
}
.week-events {
  position: relative;
  padding-bottom: 4px;
  pointer-events: none;
}

.cell {
  border-left: 1px solid $border;
  padding: 4px 6px;
  cursor: pointer;
  &:first-child {
    border-left: 0;
  }
  &:hover {
    background: #f8f9fa;
  }
  &.inactive-month {
    color: #bbb;
    background: #fcfcfc;
  }
  &.sunday:not(.inactive-month) {
    color: var(--primary);
  }
  &.selected {
    box-shadow: inset 0 0 0 2px var(--primary);
  }
}
.cell-number {
  display: inline-block;
  min-width: 1.6em;
  text-align: center;
  font-weight: bold;
  border-radius: 50%;
}
.cell.today .cell-number {
  background: var(--primary);
  color: #fff;
}

.more {
  pointer-events: auto;
  margin: 0 3px;
  padding: 0 4px;
  border: 0;
  background: none;
  font-size: 0.75rem;
  font-weight: bold;
  color: #666;
  text-align: left;
  cursor: pointer;
  &:hover {
    color: #000;
  }
}

.day-list {
  border: 1px solid $border;
  border-top: 0;
  padding: 1rem;
}
.day-list-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
}
.day-list-item {
  display: block;
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid $border;
  border-left: 4px solid var(--warning);
  border-radius: 4px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: #f8f9fa;
  }
}
.item-title {
  display: block;
  font-weight: bold;
}
.item-meta {
  display: block;
  font-size: 0.85rem;
  color: #666;
}

@media screen and (max-width: 575.98px) {
  .calendar-head h2 {
    font-size: 1.25rem;
  }
  .weekdays {
    font-size: 0.85rem;
  }
  .week {
    min-height: 56px;
  }
  .cell {
    padding: 2px 0;
    text-align: center;
  }
  .cell-number {
    font-size: 0.85rem;
  }
  .more {
    margin: 0;
    text-align: center;
    line-height: 1;
  }
}
</style>

<style lang="scss">
.event-popover {
  min-width: 320px;
  max-width: 420px;
  border: 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.2);
  .popover-body {
    padding: 0;
    color: #333;
  }
  .arrow {
    display: none;
  }
}

.event-sheet {
  .modal-dialog {
    position: fixed;
    inset: auto 0 0 0;
    margin: 0;
    max-width: none;
    transform: none !important;
  }
  .modal-content {
    border: 0;
    border-radius: 16px 16px 0 0;
    overflow: hidden;
    max-height: 85vh;
    overflow-y: auto;
  }
}
</style>
